import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import Loading from "components/Loading";

/**
 * Institution picker for users with multiple active org memberships (Q3).
 * Shown on the auth screen before redirecting to the dashboard.
 */
export default function InstitutionSelector({ onComplete }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const fn = firebase.functions().httpsCallable("listActiveInstitutions");
        const { data } = await fn();
        if (cancelled) return;

        const list = Array.isArray(data?.institutions) ? data.institutions : [];
        setInstitutions(list);

        const activeId =
          data?.activeInstitutionId ||
          list[0]?.institutionId ||
          "";
        setSelectedId(activeId);

        if (list.length <= 1) {
          onComplete?.();
        }
      } catch (err) {
        if (!cancelled) {
          // Don't block sign-in if the roster call fails (OAuth redirect / iOS).
          onComplete?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [onComplete]);

  const handleContinue = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    setError(null);
    try {
      const fn = firebase.functions().httpsCallable("switchActiveInstitution");
      await fn({ institutionId: selectedId });
      onComplete?.();
    } catch (err) {
      setError(err?.message || "Could not switch institution.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (institutions.length <= 1) {
    return null;
  }

  return (
    <Box sx={{ mt: 3, maxWidth: 420, mx: "auto" }}>
      <Typography variant="h6" gutterBottom>
        Choose your institution
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your account is linked to more than one institution. Select which one you
        are working in for this session.
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="institution-select-label">Institution</InputLabel>
        <Select
          labelId="institution-select-label"
          label="Institution"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          {institutions.map((inst) => (
            <MenuItem key={inst.institutionId} value={inst.institutionId}>
              {inst.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Button
        fullWidth
        onClick={handleContinue}
        disabled={!selectedId || submitting}
      >
        Continue
      </Button>
    </Box>
  );
}
